// A short confirmation at the foot of the screen ("Copied"), spoken by screen readers, gone after
// a moment; with Undo for a fast reversible action (D121), it stays a little longer. `copyText` puts text on the clipboard and says whether it could.
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

const SHOWN_MS = 1800

/** What a toast can undo (D121): the button's label and what it does. */
export interface ToastUndo {
  label: string
  run: () => void
}

/** How long a toast with Undo stays: long enough to read it and reach the button. */
const UNDO_MS = 6000

export function useToast(): {
  toast: ReactNode
  show: (text: string, undo?: ToastUndo) => void
} {
  const [shown, setShown] = useState<{ text: string; undo?: ToastUndo | undefined } | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const show = useCallback((text: string, undo?: ToastUndo) => {
    setShown({ text, undo })
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setShown(null), undo ? UNDO_MS : SHOWN_MS)
  }, [])
  useEffect(() => () => clearTimeout(timer.current), [])
  return {
    toast: (
      <div className="toast" role="status" hidden={shown === null}>
        {shown?.text}
        {shown?.undo ? (
          <button
            type="button"
            className="toast-undo"
            onClick={() => {
              clearTimeout(timer.current)
              // Hidden first, so what the undo says next (a new toast) stays.
              setShown(null)
              shown.undo?.run()
            }}
          >
            {shown.undo.label}
          </button>
        ) : null}
      </div>
    ),
    show,
  }
}

/** Puts text on the clipboard; false when the browser refused. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}
